/**
 * Restaurant details: the facts and actions for one saved restaurant.
 * Rendered inside the detail sheet ("stack") and inside an expanded table row ("split").
 */
import { format } from "date-fns";
import { Clock, ExternalLink, MapPin, Phone, Star } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getCuisineLabels } from "@/lib/cuisine";
import { cn } from "@/lib/utils";

import { Restaurant } from "../types/restaurants.types";
import { RestaurantRowActions } from "./restaurant-row-actions";
import { RestaurantReviews } from "./restaurant-reviews";
import { RestaurantHeart } from "./restaurant-hearts";

export const getPriceDisplay = (priceLevel?: number | null): string | null => {
  if (priceLevel === undefined || priceLevel === null) return null;
  return "$".repeat(priceLevel + 1);
};

export const getGoogleMapsUrl = (restaurant: Restaurant): string => {
  const query = encodeURIComponent(`${restaurant.name} ${restaurant.formattedAddress}`);
  return `https://www.google.com/maps/search/?api=1&query=${query}`;
};

const hostnameOf = (url: string): string => {
  try {
    return new URL(url).hostname;
  } catch {
    return url;
  }
};

interface RestaurantDetailsProps {
  restaurant: Restaurant;
  /** When set, the actions menu offers "Remove from list" instead of "Remove from favorites". */
  listId?: string;
  /** "stack" is one column (sheet); "split" is two columns on desktop (expanded row). */
  layout?: "stack" | "split";
  /** Show the name, rating and price at the top. */
  showHeading?: boolean;
  /** Hide "Remove" in the actions menu (public lists, homepage). */
  hideRemove?: boolean;
  className?: string;
}

function Fact({ icon: Icon, label, children }: { icon: React.ElementType; label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      <div className="min-w-0">
        <p className="text-[12.5px] text-muted-foreground">{label}</p>
        <div className="text-[13.5px]">{children}</div>
      </div>
    </div>
  );
}

export function RestaurantDetails({ restaurant, listId, layout = "stack", showHeading = false, hideRemove = false, className }: RestaurantDetailsProps) {
  const price = getPriceDisplay(restaurant.priceLevel);
  const cuisineTypes = getCuisineLabels(restaurant.types);
  const hours: string[] | undefined = restaurant.openingHours?.weekday_text;
  const split = layout === "split";

  return (
    <div className={cn("flex flex-col gap-4", className)}>
      {showHeading && (
        <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
          <div className="flex flex-wrap items-baseline gap-x-3">
            <span className="font-display text-xl font-medium">{restaurant.name}</span>
            {restaurant.rating && (
              <span className="inline-flex items-center gap-1 text-[13px]">
                <Star className="size-[11px] fill-star text-star-stroke" aria-hidden />
                {parseFloat(restaurant.rating).toFixed(1)}
              </span>
            )}
            {price && <span className="text-[13px]">{price}</span>}
          </div>
          <div className="flex items-center gap-2">
            <RestaurantHeart restaurantId={restaurant.id} />
            <RestaurantRowActions restaurant={restaurant} listId={listId} hideRemove={hideRemove} />
          </div>
        </div>
      )}
      {!showHeading && <RestaurantHeart restaurantId={restaurant.id} className="self-start" />}

      <div className={cn("grid gap-4", split && "md:grid-cols-2 md:gap-x-8")}>
        <div className="flex flex-col gap-3">
          <Fact icon={MapPin} label="Address">
            <span className="text-foreground">{restaurant.formattedAddress}</span>
          </Fact>
          {restaurant.phoneNumber && (
            <Fact icon={Phone} label="Phone">
              <a href={`tel:${restaurant.phoneNumber}`} className="text-brand underline underline-offset-[3px]">
                {restaurant.phoneNumber}
              </a>
            </Fact>
          )}
          {restaurant.website && (
            <Fact icon={ExternalLink} label="Website">
              <a href={restaurant.website} target="_blank" rel="noopener noreferrer" className="break-all text-brand underline underline-offset-[3px]">
                {hostnameOf(restaurant.website)}
              </a>
            </Fact>
          )}
          {hours && hours.length > 0 && (
            <Fact icon={Clock} label="Hours">
              <ul className="text-muted-foreground">
                {hours.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </Fact>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {cuisineTypes.length > 0 && (
            <div>
              <p className="mb-1 text-[12.5px] text-muted-foreground">Cuisine</p>
              <div className="flex flex-wrap gap-1.5">
                {cuisineTypes.map((type) => (
                  <Badge key={type}>{type}</Badge>
                ))}
              </div>
            </div>
          )}
          {restaurant.lists && restaurant.lists.length > 0 && (
            <div>
              <p className="mb-1 text-[12.5px] text-muted-foreground">Lists</p>
              <div className="flex flex-wrap gap-1.5">
                {restaurant.lists.map((list) => (
                  <Badge key={list.id}>{list.listName}</Badge>
                ))}
              </div>
            </div>
          )}
          {restaurant.createdAt && (
            <p className="text-[12.5px] text-muted-foreground">
              Saved {format(new Date(restaurant.createdAt), "MMMM d, yyyy")}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button asChild variant="outline" size="sm">
              <a href={getGoogleMapsUrl(restaurant)} target="_blank" rel="noopener noreferrer">
                <MapPin aria-hidden /> Open in Google Maps
              </a>
            </Button>
            {restaurant.website && (
              <Button asChild variant="outline" size="sm">
                <a href={restaurant.website} target="_blank" rel="noopener noreferrer">
                  <ExternalLink aria-hidden /> Visit website
                </a>
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-3">
        <RestaurantReviews restaurantId={restaurant.id} restaurantName={restaurant.name} />
      </div>
    </div>
  );
}

/** The block rendered inside an expanded table row. Stops clicks from reaching the row. */
export function RestaurantExpandedRow({ restaurant, listId, hideRemove }: { restaurant: Restaurant; listId?: string; hideRemove?: boolean }) {
  return (
    <div className="border-t border-border bg-background px-3 py-3" onClick={(e) => e.stopPropagation()}>
      <RestaurantDetails restaurant={restaurant} listId={listId} layout="split" showHeading hideRemove={hideRemove} />
    </div>
  );
}
